import React, { useEffect, useMemo, useState } from "react";
import { FaCrown, FaTimes } from "react-icons/fa";
import { FiBookOpen } from "react-icons/fi";
import { GrHelpBook } from "react-icons/gr";
import { GiProgression } from "react-icons/gi";
import { LuBrain } from "react-icons/lu";
import "./PaywallModal.css";

import { logEvent } from "firebase/analytics";
import { analytics } from "../../firebase";

import { Capacitor } from "@capacitor/core";

import { getFunctions, httpsCallable } from "firebase/functions";

// RevenueCat (native)
import {
  getCurrentOffering,
  purchasePackage as rcPurchasePackage,
  restorePurchases as rcRestorePurchases,
} from "../../billing/RevenueCatPaywall"; // 


// Map selected plans to Stripe price IDs (web only)
const PLAN_PRICE_IDS = {
  annual:
    process.env.NODE_ENV === "development"
      ? "price_1RwJo0CwDBYBDmZNd1oITBs3"
      : "", // TODO: add live price ID
  monthly:
    process.env.NODE_ENV === "development"
      ? "price_1RwJfJCwDBYBDmZNdEjrPKxV"
      : "", // TODO: add live price ID
};

function formatMoney(amount, currencyCode) {
  try {
    if (!currencyCode) return amount.toFixed(2);
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

export default function PaywallModal({ onClose }) {
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [loading, setLoading] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // Native offering state
  const [offering, setOffering] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState("");

  // Load offering only on native
  useEffect(() => {
    let alive = true;

    async function loadOffering() {
      if (!isNative) return;

      setPackagesLoading(true);
      setPackagesError("");

      try {
        const current = await getCurrentOffering();
        if (!alive) return;

        if (!current || !current.availablePackages || current.availablePackages.length === 0) {
          setOffering(null);
          setPackagesError("Nenhum plano disponível no momento. Tente novamente.");
        } else {
          setOffering(current);
        }
      } catch (e) {
        if (!alive) return;
        console.error("[Paywall] getOfferings failed:", e);
        setPackagesError("Não foi possível carregar os planos. Tente novamente.");
      } finally {
        if (!alive) return;
        setPackagesLoading(false);
      }
    }

    loadOffering();
    return () => {
      alive = false;
    };
  }, [isNative]);

  const prodA = (offering?.annual?.product || offering?.annual?.storeProduct);
  const prodM = (offering?.monthly?.product || offering?.monthly?.storeProduct);

  console.log("ANNUAL:", {
    id: prodA?.identifier,
    price: prodA?.price,
    priceString: prodA?.priceString,
    currencyCode: prodA?.currencyCode,
  });

  console.log("MONTHLY:", {
    id: prodM?.identifier,
    price: prodM?.price,
    priceString: prodM?.priceString,
    currencyCode: prodM?.currencyCode,
  });

  // Doc-style access: offering.monthly / offering.annual
  const selectedPackage = useMemo(() => {
    if (!isNative) return null;
    if (!offering) return null;

    // RevenueCat offering convenience props:
    // offering.monthly, offering.annual
    return selectedPlan === "annual" ? offering.annual : offering.monthly;
  }, [isNative, offering, selectedPlan]);

  // Underlying product (Capacitor uses `product` commonly; some SDKs use `storeProduct`)
  const selectedProduct = useMemo(() => {
    if (!selectedPackage) return null;
    return selectedPackage.product || selectedPackage.storeProduct || null;
  }, [selectedPackage]);

  // Price labels:
  // - web: keep your hardcoded BRL strings (Stripe handles actual price)
  // - native: use Apple localized priceString if available
  const annualPriceLabel = useMemo(() => {
    if (!isNative) return "R$199,90 / ano";
    const pkg = offering?.annual;
    const prod = pkg?.product || pkg?.storeProduct;
    const p = prod?.priceString;
    return p ? `${p} / ano` : "Plano anual";
  }, [isNative, offering]);

  const monthlyPriceLabel = useMemo(() => {
    if (!isNative) return "R$34,90 / mês";
    const pkg = offering?.monthly;
    const prod = pkg?.product || pkg?.storeProduct;
    const p = prod?.priceString;
    return p ? `${p} / mês` : "Plano mensal";
  }, [isNative, offering]);

  // Web Stripe handler
  const handleSubscribeWebStripe = async () => {

    const priceId = PLAN_PRICE_IDS[selectedPlan];
    if (!priceId) {
      alert("Plano indisponível no web no momento.");
      return;
    }

    if (analytics) {
      logEvent(analytics, "checkout_started", {
        plan: selectedPlan,
        provider: "stripe"
      });
    }

    try {
      setLoading(true);

      const functions = getFunctions();
      const startPaymentSession = httpsCallable(functions, "start_payment_session");
      const res = await startPaymentSession({ priceId });

      if (res.data?.error) {
        console.error(res.data.error);
        alert(res.data.error);
        return;
      }

      const sessionId = res.data?.sessionId;
      if (!sessionId) {
        console.error("No sessionId returned from start_payment_session");
        alert("Algo deu errado ao iniciar o pagamento. Tente novamente.");
        return;
      }

      // ✅ Lazy-load Stripe here (web only)
      const { loadStripe } = await import("@stripe/stripe-js");
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);


      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        console.error("Stripe redirect error:", error.message);
        alert(error.message || "Falha ao redirecionar para o Stripe.");
      }
    } catch (err) {
      console.error("Subscribe error:", err);
      alert("Algo deu errado. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Native RevenueCat handler
  const handleSubscribeNativeRevenueCat = async () => {
    if (analytics) {
      logEvent(analytics, "checkout_started", {
        plan: selectedPlan,
        provider: "revenuecat"
      });
    }
    if (packagesLoading) return;
    if (!selectedPackage) {
      alert("Plano indisponível. Tente novamente.");
      return;
    }

    try {
      setLoading(true);
      await rcPurchasePackage(selectedPackage);

      if (analytics) {
        logEvent(analytics, "subscription_started", {
          plan: selectedPlan,
          provider: "revenuecat"
        });
      }

      // For now we just close on success.
      // Later we’ll sync Firestore / entitlement checks.
      onClose();
    } catch (e) {
      console.error("[RevenueCat] purchase error:", e);

      // Common case: user cancelled the Apple sheet
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("cancel")) return;

      alert("Não foi possível completar a compra. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (isNative) return handleSubscribeNativeRevenueCat();
    return handleSubscribeWebStripe();
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      await rcRestorePurchases();
      alert("Compras restauradas!");
      onClose();
    } catch (e) {
      console.error("[RevenueCat] restore error:", e);
      alert("Não foi possível restaurar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const annualPerMonthLabel = useMemo(() => {
    if (!isNative) return "(R$16,66 / mês)";
  
    const pkg = offering?.annual;
    const prod = pkg?.product || pkg?.storeProduct;
    const yearly = prod?.price; // number
    const currency = prod?.currencyCode;
  
    if (typeof yearly !== "number") return "";
    const perMonth = yearly / 12;
    const formatted = formatMoney(perMonth, currency);
  
    return `(${formatted} / mês)`;
  }, [isNative, offering]);
  

  const disabled =
    loading ||
    (isNative
      ? packagesLoading || !selectedPackage
      : !PLAN_PRICE_IDS[selectedPlan]);

  return (
    <div className="paywall-modal-backdrop" onClick={onClose}>
      <div className="status-bar-paywall" />
      <div
        className="paywall-modal-content premium"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="paywall-close-btn" onClick={onClose}>
          <FaTimes size={18} />
        </button>

        <div className="paywall-header">
          <FaCrown size={38} color="var(--color-background-white)" />
          <h2>Desbloqueie o Premium</h2>
          <p>Aprenda melhor e aproveite todo o conteúdo.</p>
        </div>

        <ul className="paywall-features large">
          <li>
            <span className="feature-icon"><FiBookOpen /></span>
            Acesso ilimitado às histórias
          </li>
          <li>
            <span className="feature-icon"><GiProgression /></span>
            Melhore sua compreensão
          </li>
          <li>
            <span className="feature-icon"><LuBrain /></span>
            Expanda seu vocabulário
          </li>
          <li>
            <span className="feature-icon"><GrHelpBook /></span>
            Aprenda e entenda melhor
          </li>
        </ul>

        {/* Native offering load error */}
        {isNative && packagesError && (
          <p className="paywall-note" style={{ marginTop: 8 }}>
            {packagesError}
          </p>
        )}

        <div className="paywall-plans">
          <div
            className={`plan plan-clickable ${selectedPlan === "annual" ? "selected" : ""}`}
            onClick={() => setSelectedPlan("annual")}
          >
            <div className="plan-badge">50% de desconto</div>

            <div className="plan-info">
              <div className="plan-title-row">
                <span className="plan-title">Plano anual</span>
              </div>

              <p className="plan-note">
                <span className="plan-price-amount">{annualPriceLabel}</span>
                <span className="plan-note-subtext">{annualPerMonthLabel}</span>
              </p>
            </div>

            {selectedPlan === "annual" && (
              <div className="plan-checkmark">✓</div>
            )}
          </div>

          <div
            className={`plan plan-clickable ${selectedPlan === "monthly" ? "selected" : ""}`}
            onClick={() => setSelectedPlan("monthly")}
          >
            <div className="plan-info">
              <strong className="plan-title">Plano mensal</strong>
              <p className="plan-note">
                <span className="plan-price-amount">{monthlyPriceLabel}</span>
              </p>
            </div>

            {selectedPlan === "monthly" && (
              <div className="plan-checkmark">✓</div>
            )}
          </div>
        </div>

        <div className="beta-purchase-note">
          Teste beta: nenhuma cobrança será feita
        </div>

        <button
          className={`paywall-btn-primary ${disabled ? "disabled" : ""}`}
          onClick={handleSubscribe}
          disabled={disabled}
        >
          {loading
            ? (isNative ? "Processando..." : "Redirecionando...")
            : "Assinar Premium"}
        </button>

        <p className="paywall-note">Cancele quando quiser.</p>

        <p className="paywall-note-second">

          {isNative && (
              <>
                <button
                  type="button"
                  className="paywall-link paywall-link-button"
                  onClick={handleRestore}
                  disabled={loading || packagesLoading}
                >
                  Restaurar
                </button>
                {" • "}
              </>
            )}

          <a
            className="paywall-link"
            href="https://aprendole.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            Termos
          </a>
          {" • "}
          <a
            className="paywall-link"
            href="https://aprendole.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacidade
          </a>
        </p>


        <p className="paywall-help">
          Problemas com o pagamento? Fale com a gente: contact@aprendole.com
        </p>
      </div>
    </div>
  );
}
